/**
 * Confirmer une action irréversible — une seule fois, pour toute l'application.
 *
 * CE QUE CELA CORRIGE
 * -------------------
 * Deux confirmations existaient, toutes deux en `window.confirm`. Quatre suppressions
 * serveur n'en avaient AUCUNE : un clic, et le lien, la pièce jointe ou le filtre
 * disparaissaient. Et comme Playwright rejette automatiquement les boîtes natives, les deux
 * seuls chemins protégés n'étaient jamais exercés par les tests — un angle mort complet.
 *
 * « ÊTES-VOUS SÛR ? » N'EST PAS UNE QUESTION
 * ------------------------------------------
 * On ne demande pas à l'utilisateur d'être sûr : on lui dit CE QUI VA SE PASSER, et il
 * décide. D'où `consequences`, un tuple non vide — le type interdit d'ouvrir une
 * confirmation qui n'expliquerait rien. Le bouton porte un VERBE, jamais « OK ».
 *
 * DEUX NIVEAUX, UN SEUL COMPOSANT
 * -------------------------------
 * Le niveau ne se choisit pas : il se DÉDUIT. Fournir `attestation` ou `motif` fait passer
 * du simple au renforcé. Le cas courant reste donc court à écrire, et le cas lourd n'exige
 * pas un autre composant.
 *
 * PAS D'« ANNULER » APRÈS COUP
 * ----------------------------
 * Aucun point d'accès de restauration n'existe côté serveur. Proposer d'annuler après
 * suppression serait un mensonge d'interface : on confirme donc AVANT.
 */
import {
  createContext, useCallback, useContext, useEffect, useRef, useState,
  type JSX, type ReactNode,
} from 'react'

import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { buttonVariants } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { useT } from '@/i18n/language'

export interface DemandeConfirmation {
  /** À l'impératif, nommant l'objet : « Supprimer le lien vers #42 ? ». */
  titre: string
  /** Les effets, une phrase par effet. Au moins un — le type l'impose. */
  consequences: readonly [string, ...string[]]
  /** Ce qui reste possible ensuite : rassure autant que cela informe. */
  reversibilite?: string
  /** Libellé du bouton de confirmation : un verbe. */
  verbe: string
  ton?: 'destructif' | 'neutre'
  /** Niveau renforcé — phrase à cocher avant de pouvoir confirmer. */
  attestation?: string
  /** Niveau renforcé — motif tracé. */
  motif?: { label: string; obligatoire: boolean; aide?: string }
  /** Délai de décision avant activation, annoncé sans bloquer l'annulation. */
  graceSeconds?: number
  /** Déclencheur à restaurer lorsque la demande suit une préparation asynchrone. */
  restoreFocus?: HTMLElement
  testId?: string
}

export interface ResultatConfirmation {
  readonly confirme: boolean
  readonly motif?: string
}

const Ctx = createContext<(d: DemandeConfirmation) => Promise<ResultatConfirmation>>(
  async () => ({ confirme: false }))

export function ConfirmationProvider({ children }: { children: ReactNode }): JSX.Element {
  const { t } = useT()
  const [demande, setDemande] = useState<DemandeConfirmation | null>(null)
  const [atteste, setAtteste] = useState(false)
  const [motif, setMotif] = useState('')
  const [graceRemaining, setGraceRemaining] = useState(0)
  const resoudre = useRef<((r: ResultatConfirmation) => void) | null>(null)
  const declencheur = useRef<HTMLElement | null>(null)

  const confirmer = useCallback((d: DemandeConfirmation) => {
    declencheur.current = d.restoreFocus ?? (document.activeElement instanceof HTMLElement
      ? document.activeElement : null)
    setDemande(d)
    setAtteste(false)
    setMotif('')
    setGraceRemaining(Math.max(0, Math.ceil(d.graceSeconds ?? 0)))
    return new Promise<ResultatConfirmation>((res) => { resoudre.current = res })
  }, [])

  useEffect(() => {
    if (!demande || graceRemaining <= 0) return undefined
    const timer = window.setInterval(() => {
      setGraceRemaining((remaining) => {
        if (remaining <= 1) {
          window.clearInterval(timer)
          return 0
        }
        return remaining - 1
      })
    }, 1000)
    return () => window.clearInterval(timer)
    // Le dialogue est la durée de vie de la ressource ; la valeur utilise une mise à jour fonctionnelle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [demande])

  const fermer = (confirme: boolean): void => {
    resoudre.current?.({ confirme, ...(motif.trim() ? { motif: motif.trim() } : {}) })
    resoudre.current = null
    setDemande(null)
    setGraceRemaining(0)
  }

  // Le bouton d'action est le SEUL endroit où `disabled` reste justifié : sa condition est
  // adjacente et auto-explicative — la case à cocher est juste au-dessus.
  const bloque = Boolean(demande?.attestation && !atteste)
    || Boolean(demande?.motif?.obligatoire && !motif.trim())
    || graceRemaining > 0

  return (
    <Ctx.Provider value={confirmer}>
      {children}
      <AlertDialog open={demande !== null}
                   onOpenChange={(ouvert) => { if (!ouvert) fermer(false) }}>
        {demande ? (
          <AlertDialogContent data-testid={demande.testId ?? 'confirmation'}
            onCloseAutoFocus={(event) => {
              if (!declencheur.current) return
              event.preventDefault()
              declencheur.current.focus()
            }}>
            <AlertDialogHeader>
              <AlertDialogTitle>{demande.titre}</AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="space-y-2">
                  <ul className="list-disc space-y-1 pl-4">
                    {demande.consequences.map((c) => <li key={c}>{c}</li>)}
                  </ul>
                  {demande.reversibilite ? (
                    <p className="text-foreground">{demande.reversibilite}</p>
                  ) : null}
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>

            {demande.attestation ? (
              <label className="flex items-start gap-2 text-sm">
                <input type="checkbox" data-testid="confirmation-attestation"
                       className="mt-0.5 size-4" checked={atteste}
                       onChange={(e) => setAtteste(e.target.checked)} />
                <span>{demande.attestation}</span>
              </label>
            ) : null}

            {demande.motif ? (
              <div className="space-y-2">
                <Label htmlFor="confirmation-motif">
                  {demande.motif.label}
                  {demande.motif.obligatoire ? (
                    <span aria-hidden="true" className="text-destructive"> *</span>
                  ) : null}
                </Label>
                <Textarea id="confirmation-motif" data-testid="confirmation-motif" rows={2}
                          value={motif} onChange={(e) => setMotif(e.target.value)}
                          aria-required={demande.motif.obligatoire || undefined} />
                {demande.motif.aide ? (
                  <p className="text-xs text-muted-foreground">{demande.motif.aide}</p>
                ) : null}
              </div>
            ) : null}

            {graceRemaining > 0 ? (
              <p className="text-sm text-muted-foreground" role="status" aria-live="polite"
                data-testid="confirmation-grace">
                {t('confirm.graceRemaining',
                  'Confirmation disponible dans {{seconds}} s', { seconds: graceRemaining })}
              </p>
            ) : null}

            <AlertDialogFooter>
              <AlertDialogCancel data-testid="confirmation-annuler" onClick={() => fermer(false)}>
                {t('confirm.cancel', 'Annuler')}
              </AlertDialogCancel>
              <AlertDialogAction
                data-testid="confirmation-valider"
                disabled={bloque}
                className={cn(demande.ton === 'destructif'
                  && buttonVariants({ variant: 'destructive' }))}
                onClick={() => fermer(true)}>
                {demande.verbe}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        ) : null}
      </AlertDialog>
    </Ctx.Provider>
  )
}

/** Demande confirmation. Rend une promesse : `if ((await confirmer({…})).confirme) …`. */
export function useConfirmation(): (d: DemandeConfirmation) => Promise<ResultatConfirmation> {
  return useContext(Ctx)
}
